import { FunctionComponent, SyntheticEvent, useEffect, useState } from 'react'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import { useTranslation } from 'react-i18next'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import { OccasionalSlotsPlaces, RegularSlot, slotIdAndDateAndChildId } from '@/types/Slots'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { useLazyQuery, useMutation } from '@apollo/client'
import {
    REQUEST_UPDATE_REGULAR,
    UPDATE_MULTIPLE_REGULAR,
    UPDATE_UNIQUE_REGULAR
} from '@/modules/API/slot/regularUpdate'
import moment, { Moment } from 'moment/moment'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select'
import styles from './updateUniqueRegularSlot.module.scss'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple'
import TextArea from '@/components/_custom/TextArea/textArea'
import { CalendarMonth } from '@mui/icons-material'
import TimePicker from '@/components/_custom/Picker/timePicker/timePicker'
import { getDaycareClosing, getDaycareDaysSelectOptions, getDaycareOpening } from '@/modules/helper/daycareHelper'
import { Daycare } from '@/types/Daycare'
import { format } from 'date-fns'
import { getLocalFromLanguage } from '@/modules/helper/languageHelper.ts'
import SelectMultipleDateDialogComponent from '../../occasionalSlot/occasionalSlotForm/components/SelectMultipleDateDialog/selectMultipleDateDialog.tsx'
import {
    Autocomplete,
    AutocompleteRenderGetTagProps,
    AutocompleteRenderInputParams,
    Chip,
    TextField
} from '@mui/material'
import Checkbox from '@/components/_custom/Checkbox/Checkbox.tsx'
import { GET_REGULAR_SLOTS } from '@/modules/API/slot/regularSlotHandler.ts'
import { CalendarEventType } from '@/types/Calendar.ts'
import DatePickerComponent from '@/components/_custom/Picker/datePicker/datePicker.tsx'
import { getDatesBetweenTwoDates } from '../confirmDeleteRegularSlot/confirmDeleteRegularSlotDialog.tsx'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique.tsx'
import { ClosingDay } from '@/types/ClosingDays.ts'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import { DAY_FORMAT } from '../../occasionalSlot/models/constants.ts'
import { useDialog } from '@/hooks/useDialog/useDialog.tsx'

interface UpdateRegularSlotProps {
    open: boolean
    handleClose: () => void
    isParent: boolean
    regularSlots: RegularSlot[]
    date: Date | null
    daycare: Daycare
    closeParent: () => void
    refetchData: () => void
    lastRegularSlotDate: string
    places: OccasionalSlotsPlaces[]
    closingDays: ClosingDay[]
}

const UpdateUniqueRegularSlot: FunctionComponent<UpdateRegularSlotProps> = ({
    open,
    handleClose,
    isParent,
    regularSlots,
    date,
    closeParent,
    daycare,
    refetchData,
    lastRegularSlotDate,
    places,
    closingDays
}: UpdateRegularSlotProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [chooseDate, setChooseDate] = useState<boolean>(false)
    const [dateList, setDateList] = useState<string[]>([])

    const [selectPeriod, setSelectPeriod] = useState<boolean>(false)
    const [startDate, setStartDate] = useState<string | null>(null)
    const [endDate, setEndDate] = useState<string | null>(null)

    const [chooseDays, setChooseDays] = useState<boolean>(false)
    const [selectedDays, setSelectedDays] = useState<string[]>([])

    const [newStart, setNewStart] = useState<string>(moment(regularSlots[0].start, 'HH:mm:ss').format('HH:mm'))
    const [newEnd, setNewEnd] = useState<string>(moment(regularSlots[0].end, 'HH:mm:ss').format('HH:mm'))

    const [reason, setReason] = useState<string>('')
    const [selectChilds, setSelectChilds] = useState<string[]>(
        regularSlots.map((slot: RegularSlot) => slot.childId.toString())
    )

    const [place, setPlace] = useState<string>(regularSlots[0].placeId ? regularSlots[0].placeId.toString() : '')

    const selectOptionsPlace: SelectOptionsDeprecated = [
        {
            options: [
                ...places.map((place: OccasionalSlotsPlaces): SelectOptionDeprecated => {
                    return {
                        name: place.name.valueOf(),
                        value: place.id.toString()
                    }
                }),
                {
                    name: t('noPlaces'),
                    value: ''
                }
            ]
        }
    ]

    const selectOptionsChild: SelectOptionsDeprecated = [
        {
            options: regularSlots.map((slot: RegularSlot): SelectOptionDeprecated => {
                return {
                    name: slot.childName + (slot.childComplement ? ' - ' + slot.childComplement : ''),
                    value: slot.childId.toString()
                }
            })
        }
    ]

    const selectDays: SelectOptionsDeprecated = getDaycareDaysSelectOptions(daycare, t)

    const [updateUniqueRegular, { loading: loadingUpdate }] = useMutation(UPDATE_UNIQUE_REGULAR, {
        onCompleted: () => {
            refetchData()
            closeParent()
            handleClose()

            showNotificationSuccess(t('updateUniqueRegularSuccess'))
        },
        onError: () => {
            showNotificationError(t('errorUpdateUniqueRegular'))
        }
    })

    const [updateMultipleRegular, { loading: loadingUpdateMultiple }] = useMutation(UPDATE_MULTIPLE_REGULAR, {
        onCompleted: () => {
            refetchData()
            closeParent()
            handleClose()

            showNotificationSuccess(t('updateUniqueRegularSuccess'))
        },
        onError: () => {
            showNotificationError(t('errorUpdateUniqueRegular'))
        }
    })

    function updateAllUniqueRegular(): void {
        if ((chooseDate && dateList.length > 0) || selectPeriod) {
            const slotToRequest: slotIdAndDateAndChildId[] = []

            const dateListToDelete: string[] =
                selectPeriod && startDate && endDate
                    ? getDatesBetweenTwoDates(startDate, endDate, selectedDays, closingDays)
                    : dateList

            dateListToDelete.forEach((date: string) => {
                regularSlotsInCalendar
                    .filter((slot: slotIdAndDateAndChildId): boolean => selectChilds.includes(slot.childId.toString()))
                    .filter((slot: slotIdAndDateAndChildId): boolean => slot.date === date)
                    .forEach((slot: slotIdAndDateAndChildId) => {
                        slotToRequest.push(slot)
                    })
            })

            updateMultipleRegular({
                variables: {
                    slots: slotToRequest,
                    start: newStart,
                    end: newEnd,
                    daycareId: daycare.id,
                    placeId: place === '' ? undefined : Number(place)
                }
            })
        } else {
            regularSlots
                .filter((slot: RegularSlot): boolean => selectChilds.includes(slot.childId.toString()))
                .forEach((slot: RegularSlot) => {
                    updateUniqueRegular({
                        variables: {
                            regular_slot_id: slot.id,
                            date: moment(date).format('DD-MM-YYYY'),
                            start: newStart,
                            end: newEnd,
                            daycare_id: slot.daycareId,
                            placeId: place === '' ? undefined : Number(place)
                        }
                    })
                })
        }
    }

    const [requestUpdateRegular, { loading: loadingRequest }] = useMutation(REQUEST_UPDATE_REGULAR, {
        onCompleted: () => {
            refetchData()
            closeParent()
            handleClose()

            showNotificationSuccess(t('requestUpdateUniqueRegularSuccess'))
        },
        onError: () => {
            showNotificationError(t('errorRequestUpdateUniqueRegular'))
        }
    })

    function requestAllAbsences(): void {
        if ((chooseDate && dateList.length > 0) || selectPeriod) {
            const slotToRequest: slotIdAndDateAndChildId[] = []

            const dateListToDelete: string[] =
                selectPeriod && startDate && endDate
                    ? getDatesBetweenTwoDates(startDate, endDate, selectedDays, closingDays)
                    : dateList

            dateListToDelete.forEach((date: string) => {
                regularSlotsInCalendar
                    .filter((slot: slotIdAndDateAndChildId): boolean => selectChilds.includes(slot.childId.toString()))
                    .filter((slot: slotIdAndDateAndChildId): boolean => slot.date === date)
                    .forEach((slot: slotIdAndDateAndChildId) => {
                        slotToRequest.push(slot)
                    })
            })

            slotToRequest.forEach((slot: slotIdAndDateAndChildId) => {
                requestUpdateRegular({
                    variables: {
                        regular_slot_id: slot.slotId,
                        date: slot.date,
                        start: newStart,
                        end: newEnd,
                        reason: reason,
                        daycare_id: daycare.id
                    }
                })
            })
        } else {
            regularSlots
                .filter((slot: RegularSlot) => selectChilds.includes(slot.childId.toString()))
                .forEach((slot: RegularSlot) => {
                    requestUpdateRegular({
                        variables: {
                            regular_slot_id: slot.id,
                            date: moment(date).format('DD-MM-YYYY'),
                            start: newStart,
                            end: newEnd,
                            reason: reason,
                            daycare_id: slot.daycareId
                        }
                    })
                })
        }
    }

    function handleCheckChooseDate(checked: boolean): void {
        if (dateList.length === 0) setDateList([moment(date).format('DD-MM-YYYY')])
        if (checked) openSelectMultipleDateDialog()
        setChooseDate(checked)

        if (selectPeriod) {
            setSelectPeriod(false)
            setStartDate(null)
            setEndDate(null)
        }
    }

    function handleDateRemove(dateToRemove: string): void {
        setDateList(dateList.filter((date) => date !== dateToRemove))
    }

    function handleCheckSelectPeriod(checked: boolean): void {
        if (startDate === null || endDate === null) {
            setStartDate(moment(date).format('DD-MM-YYYY'))
            setEndDate(moment(date).format('DD-MM-YYYY'))
        }

        setSelectPeriod(checked)

        if (chooseDate) {
            setChooseDate(false)
            setDateList([])
        }
    }

    const [otherDisabledDates, setOtherDisabledDates] = useState<Date[]>([])

    const [regularSlotsInCalendar, setRegularSlotsInCalendar] = useState<slotIdAndDateAndChildId[]>([])

    const [getRegularSlots, { data: dataRegularSlots, loading: loadingRegularSlots }] = useLazyQuery(GET_REGULAR_SLOTS)

    useEffect(() => {
        getRegularSlots({
            variables: {
                from: moment().format('DD-MM-YYYY'),
                to: lastRegularSlotDate,
                daycareId: daycare.id,
                childIds: selectChilds.map((childId: string): number => Number(childId))
            }
        })
    }, [selectChilds])

    useEffect(() => {
        const dateToShow: string[] = []
        const regularSlotsCalendar: slotIdAndDateAndChildId[] = []

        dataRegularSlots?.getRegularSlots.map((regularSlot: RegularSlot): void => {
            if (regularSlot.type === CalendarEventType.REGULAR) {
                dateToShow.push(regularSlot.date)

                regularSlotsCalendar.push({
                    slotId: regularSlot.id,
                    date: regularSlot.date,
                    childId: regularSlot.childId
                })
            }
        })

        setRegularSlotsInCalendar(regularSlotsCalendar)

        const start: Moment = moment().startOf('month')
        const end: Moment = start.clone().add(1, 'year').endOf('month')

        const result: Date[] = []

        let currentDate: Moment = start.clone()
        while (currentDate.isSameOrBefore(end)) {
            const formatted: string = currentDate.format('DD-MM-YYYY')
            if (!dateToShow.includes(formatted)) {
                result.push(currentDate.toDate())
            }
            currentDate = currentDate.add(1, 'days')
        }

        setOtherDisabledDates(result)
    }, [dataRegularSlots])

    const range: number = daycare.merger ? Number(daycare.bookRange.split('_')[1] || '15') : 15

    const disabled: boolean =
        (newStart === moment(regularSlots[0].start, 'HH:mm:ss').format('HH:mm') &&
            newEnd === moment(regularSlots[0].end, 'HH:mm:ss').format('HH:mm') &&
            place === (regularSlots[0].placeId ? regularSlots[0].placeId.toString() : '')) ||
        loadingUpdate ||
        loadingUpdateMultiple ||
        loadingRequest ||
        loadingRegularSlots

    const { dialog: SelectMultipleDateDialog, openDialog: openSelectMultipleDateDialog } = useDialog({
        Component: SelectMultipleDateDialogComponent,
        props: () => ({
            daycare,
            dateList,
            setDateList,
            startDate: moment(date).format(DAY_FORMAT),
            closingDays: [],
            isParent,
            otherDisabledDates
        }),
        dialogTitle: false
    })

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader
                parent={isParent}
                title={isParent ? t('requestUpdateUniqueRegular') : t('declareUpdateUniqueRegular')}
            />
            <DialogBody className={styles.DialogBody}>
                <div className={styles.Container}>
                    {chooseDate && dateList.length > 0 ? (
                        <Autocomplete
                            multiple
                            options={dateList}
                            value={dateList}
                            onChange={(_event: SyntheticEvent, newValue: string[]) => setDateList(newValue)}
                            renderTags={(value: string[], getTagProps: AutocompleteRenderGetTagProps) =>
                                value.map((option: string, index: number) => {
                                    const { key, ...tagProps } = getTagProps({ index })

                                    return (
                                        <Chip
                                            key={key}
                                            label={option}
                                            {...tagProps}
                                            onDelete={() => handleDateRemove(option)}
                                        />
                                    )
                                })
                            }
                            renderInput={(params: AutocompleteRenderInputParams) => (
                                <TextField
                                    {...params}
                                    variant='outlined'
                                    rows={1}
                                    multiline={true}
                                    label={t('dateList')}
                                />
                            )}
                            className={isParent ? styles.AutocompleteParent : styles.AutocompleteDaycare}
                            onOpen={openSelectMultipleDateDialog}
                        />
                    ) : selectPeriod && startDate && endDate ? (
                        <div className={styles.InputContainer}>
                            <div className={styles.Input}>
                                <DatePickerComponent
                                    className={styles.UserInput}
                                    value={startDate.valueOf()}
                                    setValue={setStartDate}
                                    maxDate={endDate.valueOf()}
                                    disablePast={true}
                                    label={t('fromDate')}
                                    parent={isParent}
                                />
                                <DatePickerComponent
                                    className={styles.UserInput}
                                    value={endDate.valueOf()}
                                    setValue={setEndDate}
                                    minDate={startDate.valueOf()}
                                    label={t('toDate')}
                                    parent={isParent}
                                />
                            </div>
                            <div className={styles.VerticalInput}>
                                <div className={styles.CheckboxInput}>
                                    <Checkbox
                                        text={t('chooseRecurrence')}
                                        checked={chooseDays}
                                        onChange={(checked: boolean) => setChooseDays(checked)}
                                        className={styles.Checkbox}
                                        parent={isParent}
                                    />
                                </div>
                                {chooseDays && (
                                    <SelectMultiple
                                        selectOptions={selectDays}
                                        label={t('days')}
                                        selectValues={selectedDays}
                                        setSelectValues={setSelectedDays}
                                        className={styles.UserInput}
                                        isParent={isParent}
                                    />
                                )}
                            </div>
                        </div>
                    ) : (
                        <>
                            <CalendarMonth
                                className={`${styles.Icon} ${isParent ? styles.IconParent : styles.IconDaycare}`}
                            />
                            <div className={styles.TextContainer}>
                                {date && (
                                    <p className={styles.FirstText}>
                                        {format(date, 'EEEE dd MMMM yyyy', {
                                            locale: getLocalFromLanguage()
                                        })}
                                    </p>
                                )}
                                <p className={styles.SecondText}>
                                    {t('from') +
                                        ' ' +
                                        moment(regularSlots[0].start, 'HH:mm:ss').format('HH:mm') +
                                        ' ' +
                                        t('to').toLowerCase() +
                                        ' ' +
                                        moment(regularSlots[0].end, 'HH:mm:ss').format('HH:mm')}
                                </p>
                            </div>
                        </>
                    )}
                </div>
                <div className={styles.CheckboxContainer}>
                    <Checkbox
                        text={t('selectOtherDates')}
                        checked={chooseDate}
                        onChange={(checked: boolean) => handleCheckChooseDate(checked)}
                        className={styles.Checkbox}
                        parent={isParent}
                    />
                    <Checkbox
                        text={t('selectPeriod')}
                        checked={selectPeriod}
                        onChange={(checked: boolean) => handleCheckSelectPeriod(checked)}
                        className={styles.Checkbox}
                        parent={isParent}
                    />
                </div>
                <div className={styles.BreakLine}></div>
                {regularSlots.length > 1 && (
                    <div className={styles.Input}>
                        <SelectMultiple
                            selectOptions={selectOptionsChild}
                            selectValues={selectChilds}
                            setSelectValues={setSelectChilds}
                            className={styles.UserInput}
                            label={t('childs')}
                            isParent={isParent}
                        />
                    </div>
                )}
                <div className={styles.Container}>
                    <div className={styles.InputContainer}>
                        <div className={styles.Input}>
                            <TimePicker
                                value={newStart}
                                setValue={setNewStart}
                                parent={isParent}
                                className={styles.UserInput}
                                label={t('startTime')}
                                maxHour={Number(
                                    moment(
                                        getDaycareClosing(daycare, (moment(date, 'DD-MM-YYYY').day() + 6) % 7),
                                        'HH:mm'
                                    ).format('HH')
                                )}
                                minHour={Number(
                                    moment(
                                        getDaycareOpening(daycare, (moment(date, 'DD-MM-YYYY').day() + 6) % 7),
                                        'HH:mm'
                                    ).format('HH')
                                )}
                                maxMinute={Number(
                                    moment(
                                        getDaycareClosing(daycare, (moment(date, 'DD-MM-YYYY').day() + 6) % 7),
                                        'HH:mm'
                                    ).format('mm')
                                )}
                                minMinute={Number(
                                    moment(
                                        getDaycareOpening(daycare, (moment(date, 'DD-MM-YYYY').day() + 6) % 7),
                                        'HH:mm'
                                    ).format('mm')
                                )}
                                minuteToDisable={range}
                            />
                            <TimePicker
                                value={newEnd}
                                setValue={setNewEnd}
                                parent={isParent}
                                className={styles.UserInput}
                                label={t('endTime')}
                                maxHour={Number(
                                    moment(
                                        getDaycareClosing(daycare, (moment(date, 'DD-MM-YYYY').day() + 6) % 7),
                                        'HH:mm'
                                    ).format('HH')
                                )}
                                minHour={Number(
                                    moment(
                                        getDaycareOpening(daycare, (moment(date, 'DD-MM-YYYY').day() + 6) % 7),
                                        'HH:mm'
                                    ).format('HH')
                                )}
                                maxMinute={Number(
                                    moment(
                                        getDaycareClosing(daycare, (moment(date, 'DD-MM-YYYY').day() + 6) % 7),
                                        'HH:mm'
                                    ).format('mm')
                                )}
                                minMinute={Number(
                                    moment(
                                        getDaycareOpening(daycare, (moment(date, 'DD-MM-YYYY').day() + 6) % 7),
                                        'HH:mm'
                                    ).format('mm')
                                )}
                                minuteToDisable={range}
                            />
                        </div>
                    </div>
                </div>
                {isParent ? (
                    <div className={styles.TextAreaInput}>
                        <p className={styles.TextAreaTitle}>{t('updateUniqueRegularReason')}</p>
                        <TextArea
                            placeholder={t('updateUniqueRegularReason')}
                            value={reason}
                            onChange={setReason}
                            maxCount={100}
                            className={styles.TextArea}
                            parent={isParent}
                        />
                    </div>
                ) : (
                    <div className={styles.Container}>
                        <div className={styles.InputContainer}>
                            <div className={styles.Input}>
                                <SelectUnique
                                    selectOptions={selectOptionsPlace}
                                    selectValue={place}
                                    setSelectValue={setPlace}
                                    formClassName={styles.UserInput}
                                    label={t('places')}
                                />
                            </div>
                        </div>
                    </div>
                )}
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon type={'close'} onClick={handleClose} parent={isParent} className={styles.Button} />
                    <ButtonTextIcon
                        type={'confirm'}
                        onClick={isParent ? requestAllAbsences : updateAllUniqueRegular}
                        parent={isParent}
                        className={styles.Button}
                        disabled={disabled}
                    />
                </div>
            </DialogBody>
            {SelectMultipleDateDialog}
        </Dialog>
    )
}

export default UpdateUniqueRegularSlot
